import * as React from 'react'
import { useEffect, useMemo, useRef } from 'react'
import { useInViewport } from 'ahooks'
import mergeRefs from 'merge-refs'

import { preloadImage } from '@/lib/images.ts'
import { queryClient } from '@/clients/query-client.ts'
import { useFetchThumbnail } from '@/features/files/hooks/use-get-file-thumbnail.ts'
import { DocumentSkeleton } from '@/routes/_authenticated/documents/-components/DocumentSkeleton.tsx'

export interface ThumbnailProps
  extends React.InputHTMLAttributes<HTMLImageElement> {
  fileId?: string | null
  pageNum?: number
  pageCount?: number
  res?: 'high' | 'low'
}

export const Thumbnail = React.forwardRef<HTMLImageElement, ThumbnailProps>(
  ({ fileId, pageNum, res, pageCount, ...rest }, ref) => {
    const inViewportRef = useRef(null)
    const [inViewport] = useInViewport(inViewportRef)
    const fetchDocumentThumbnailQuery = useFetchThumbnail(
      {
        fileId: fileId!,
        pageNum: pageNum ?? 1,
        res: res ?? 'low',
      },
      { enabled: !!fileId && !!inViewport }
    )

    const downloadUrl = fetchDocumentThumbnailQuery?.data?.success
      ? fetchDocumentThumbnailQuery?.data?.data?.downloadUrl
      : null

    // preload
    const preloadedImagesSet = useMemo(() => new Set(), [])
    useEffect(() => {
      if (!pageCount || !fileId || !inViewport) return

      const queryOptions = useFetchThumbnail.queryOptions({
        fileId,
        res,
        pageNum: Math.min((pageNum ?? 0) + 1, pageCount),
      })

      if (preloadedImagesSet.has(JSON.stringify(queryOptions.queryKey))) return

      queryClient.ensureQueryData(queryOptions).then(() => {
        const data = queryClient.getQueryData(queryOptions.queryKey)
        if (!data?.success) return
        const {
          data: { downloadUrl },
        } = data ?? {}

        preloadImage(downloadUrl)
        preloadedImagesSet.add(JSON.stringify(queryOptions.queryKey))
      })
    }, [fileId, inViewport, pageCount, pageNum, preloadedImagesSet, res])

    if (fileId && !inViewport) {
      return <DocumentSkeleton ref={mergeRefs(ref, inViewportRef)} />
    }

    if (!fileId || fetchDocumentThumbnailQuery.isLoading) {
      return <DocumentSkeleton ref={mergeRefs(ref, inViewportRef)} />
    }

    if (!downloadUrl) {
      return null
    }

    return (
      <img
        ref={mergeRefs(ref, inViewportRef)}
        key={downloadUrl}
        src={downloadUrl}
        {...rest}
      />
    )
  }
)
Thumbnail.displayName = 'Thumbnail'
